import React, { useState } from 'react';
import AdminSearchTable from '../Admin/AdminSearchTable'
import { useNavigate } from 'react-router-dom';
import EmptyHeader from '../Shared/EmptyHeader';
import { useAuth } from "../../src/Context/AuthContext";

const PolicySearchSection = () => {
    //State Variables
    const [ownerFirstName, setOwnerFirstName] = useState('');
    const [ownerLastName, setOwnerLastName] = useState('');
    const [ownerEntityName, setOwnerEntityName] = useState('');

    const [insuredFirstName, setInsuredFirstName] = useState('');
    const [insuredLastName, setInsuredLastName] = useState('');

    const [polNo, setPolNo] = useState('');
    const [results, setResults] = useState([]);
    const [noItemsFound, setNoItemsFound] = useState(false);
    const navigate = useNavigate();
    const { logout } = useAuth();

    //ENV Variables
    const apiURL = import.meta.env.VITE_API;

    const columns = [
        { label: 'Policy Number', accessor: 'polNo' },
        { label: 'Description', accessor: 'description' },
        { label: 'Owner / Annuitant', accessor: 'displayName' },
        { label: 'Issue Date', accessor: 'issDate' },
        { label: 'Portal UserName', accessor: 'portalUserName' }
    ];

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d) ? 'N/A' : d.toLocaleDateString();
    };

    const searchByInsuredName = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${apiURL}/admin/searchPolicy/ByInsuredName?firstName=${insuredFirstName}&lastName=${insuredLastName}`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (res.status === 404) {
                // No items found (API should return 404 in this case)
                setResults([]); // Clear results if needed
                setNoItemsFound(true); // Custom state to show message
                return;
            }

            if (!res.ok) {
                const text = await res.text(); // log raw error response
                throw new Error(`Request failed: ${res.status} ${res.statusText}\n${text}`);
            }

            const data = await res.json();
            setResults(data);
            setNoItemsFound(data.length === 0);
        } catch (err) {
            console.error('Error fetching by name:', err);
            alert("An error occurred while fetching data. Check the console for details.");
        }
    };

    const searchByOwnerName = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${apiURL}/admin/searchPolicy/ByOwnerName?firstName=${ownerFirstName}&lastName=${ownerLastName}&entityName=${ownerEntityName}`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (res.status === 404) {
                // No items found (API should return 404 in this case)
                setResults([]); // Clear results if needed
                setNoItemsFound(true); // Custom state to show message
                return;
            }

            if (!res.ok) {
                const text = await res.text(); // log raw error response
                throw new Error(`Request failed: ${res.status} ${res.statusText}\n${text}`);
            }

            const data = await res.json();
            setResults(data);
            setNoItemsFound(data.length === 0);
        } catch (err) {
            console.error('Error fetching by name:', err);
            alert("An error occurred while fetching data. Check the console for details.");
        }
    };

    const searchByPolNo = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${apiURL}/admin/searchPolicy/by-polno?polNo=${polNo}`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");

            }
            if (res.status === 404) {
                // No items found (API should return 404 in this case)
                setResults([]); // Clear results if needed
                setNoItemsFound(true); // Custom state to show message
                return;
            }

            if (!res.ok) {
                const text = await res.text(); // log raw error response
                throw new Error(`Request failed: ${res.status} ${res.statusText}\n${text}`);
            }

            const data = await res.json();

            if (data.length === 1) {
                navigate(`/policy/${data[0].polNo}`)
            }

            setResults(data);
            setNoItemsFound(data.length === 0);
        } catch (err) {
            console.error('Error fetching by policy number:', err);
            alert("An error occurred while fetching data. Check the console for details.");
        }
    };

    return (
        <div className="admin-search-page" id="policy-search-container">

            <div className="admin-search-columns">
                <div className="search-card">
                    <h5>Search by Insured/Annuitant Name</h5>
                    <form className="baseForm" onSubmit={searchByInsuredName}>
                        <label className="baseLabel">First Name</label>
                        <input
                            type="text"
                            value={insuredFirstName}
                            className="baseInput"
                            onChange={(e) => setInsuredFirstName(e.target.value)}
                            placeholder="e.g. John"
                        />
                        <label className="baseLabel">Last Name</label>
                        <input
                            type="text"
                            value={insuredLastName}
                            className="baseInput"
                            onChange={(e) => setInsuredLastName(e.target.value)}
                            placeholder="e.g. Smith"
                        />
                        <button type="submit" className="baseButton">Search</button>
                    </form>
                </div>

                <div className="search-card">
                    <h5>Search by Owner Name</h5>
                    <form className="baseForm" onSubmit={searchByOwnerName}>
                        <label className="baseLabel">First Name</label>
                        <input
                            type="text"
                            value={ownerFirstName}
                            className="baseInput"
                            onChange={(e) => setOwnerFirstName(e.target.value)}
                            placeholder="e.g. John"
                        />
                        <label className="baseLabel">Last Name</label>
                        <input
                            type="text"
                            value={ownerLastName}
                            className="baseInput"
                            onChange={(e) => setOwnerLastName(e.target.value)}
                            placeholder="e.g. Smith"
                        />
                        <label className="baseLabel">Entity Name</label>
                        <input
                            type="text"
                            value={ownerEntityName}
                            className="baseInput"
                            onChange={(e) => setOwnerEntityName(e.target.value)}
                            placeholder="e.g. Example Trust"
                        />
                        <button type="submit" className="baseButton">Search</button>
                    </form>
                </div>

                <div className="search-card">
                    <h5>Search by Policy Number</h5>
                    <form className="baseForm" onSubmit={searchByPolNo}>
                        <label className="baseLabel">Policy Number</label>
                        <input
                            type="text"
                            value={polNo}
                            className="baseInput"
                            onChange={(e) => setPolNo(e.target.value)}
                            placeholder="e.g. 0000000001P1"
                        />
                        <button className="baseButton" type="submit" >Search</button>
                    </form>
                </div>
            </div>

            <EmptyHeader />

            {results.length > 0 && (
                <AdminSearchTable columns={columns} data={results} /> 
            )}

            {noItemsFound && (
                <div className="no-items-message">
                    No policies found.
                </div>
            )}
        </div>
    );
};

export default PolicySearchSection;
